import { PageHeading } from '@quad/bootstrap-react';
import { useEffect, useState } from 'react';
import { Container, Stack } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import AppManagerClient from '../../api/AppManagerClient';
import { ServerEventLog } from '../../components/EventLog/ServerEventLog';
import { ServerPermissions } from '../../components/Permissions/ServerPermissions';
import { ServerAgents } from '../../components/ServerAgents/ServerAgents';
import { ServerApplicationListing } from '../../components/ServerApplicationListing/ServerApplicationListing';
import { AccordionWrapper } from '../../components/UIExtentions/Accordion';
import withHasPermission from '../../hoc/withHasPermission';
import { Permission } from '../../permissions/permission';
import { UserPrincipal } from '../../permissions/userPrincipal';
import { ServerClientEventSummary, ServerPermissionClientEventSummary } from '../../signalr/models';
import signalREventsClient from '../../signalr/SignalREventsClient';
import ScrollPageContent from '../shared/ScrollPageContent';
import CertificatesEnhancedTable from '../../components/Certificates/CertificateEnhancedTable';
import { ICertificate, IServer } from '../../api/models';
import { ServerInfoView } from '../../components/ServerDetails/ServerInfoView';
import { ActionButton } from '../../components/UIExtentions/Buttons';
import { ServerClientEventSummaryHandler, ServerClientEventSummaryHandlerArgs } from '../../components/ServerDetails/ServerClientEventSummaryHandler';
import { EventType } from '../../constants/EventType';
import { PopupProps } from '../shared/Popup';
import { ApplicationServerManagementToast } from '../../components/ApplicationServerManagement/ApplicationServerManagementToast';

export const ServerDetails = withHasPermission(() => {
    const serverName = useParams<'name'>().name!;
    const [userPrincipal, setUserPrincipal] = useState(new UserPrincipal([]));
    const [serverIsLoading, setServerIsLoading] = useState(true);
    const [serverErrorMessage, setServerErrorMessage] = useState('');
    const [server, setServer] = useState({} as IServer);
    const [certificates, setCertificates] = useState(Array<ICertificate>());
    const [certificatesAreLoading, setCertificatesAreLoading] = useState(true);
    const [updateSpin, setUpdateSpin] = useState(false);
    const [popupProps, setPopupProps] = useState<PopupProps | undefined>();

    const getUserPrincipal = async (serverName: string) => {
        const permissionsResult = await AppManagerClient.getServerUserPermissions(serverName);
        const myUserPrincipal = new UserPrincipal(permissionsResult);
        setUserPrincipal(myUserPrincipal);
    };

    const getServer = async (serverName: string) => {
        setServerIsLoading(true);
        try {
            const serverResult = await AppManagerClient.getServerByName(serverName);
            if (serverResult) {
                setServer(serverResult);
            }
        } catch (err: any) {
            setServerErrorMessage(err.message);
        } finally {
            setServerIsLoading(false);
        }
    };

    const loadCertificates = async (serverId: number) => {
        try {
            const certs = await AppManagerClient.getCertificates({
                top: null,
                select: 'friendlyName,commonName,thumbprint,validFrom,validTo,issuer,email,keyUsages',
                expand: null,
                filter: `serverCertificates/any(c: c/serverId eq ${serverId})`,
                orderBy: null,
                skip: null,
            });
            setCertificates(certs);
        } finally {
            setCertificatesAreLoading(false);
        }
    };

    const showPopup = function (props: PopupProps) {
        setPopupProps(props);
    };

    useEffect(() => {
        getUserPrincipal(serverName);
        getServer(serverName);

        function isForMe(data: ServerPermissionClientEventSummary | ServerClientEventSummary) {
            return data.targetServer === serverName;
        }

        signalREventsClient.onServerPermissionEvent.setHook(`ServerDetails_${serverName}`, (data) => {
            if (isForMe(data)) {
                getUserPrincipal(data.targetServer);
            }
        });

        const onTimeout = function (data: ServerClientEventSummary) {
            showPopup({
                header: `Operation timed out on [${data.targetServer}].`,
                body: `Operation: [${data.type}]`,
                type: 'danger',
                autohide: true,
            });
        };

        const serverUpdateHandler = new ServerClientEventSummaryHandler(serverName, {
            addTypes: [EventType.ServerUpdateTriggered],
            successTypes: [EventType.ServerUpdateSucceeded],
            failedTypes: [EventType.ServerUpdateFailed],
            emptyCallback: () => setUpdateSpin(false),
            addCallback: () => setUpdateSpin(true),
            timeoutCallback: (data) => onTimeout(data),
        } as ServerClientEventSummaryHandlerArgs);

        signalREventsClient.onServerEvent.setHook(`ServerDetails_${serverName}`, (data) => {
            if (isForMe(data)) {
                serverUpdateHandler.handleItem(data);
                if (data.type === EventType.ServerUpdateSucceeded) {
                    getServer(serverName);
                }
            }
        });
    }, [serverName]);

    useEffect(() => {
        if (server && server.id) {
            loadCertificates(server.id);
        }
    }, [server]);

    return (
        <ScrollPageContent>
            <PageHeading>
                <div className="col">
                    <h1>
                        <i className="fa fa-server"></i> {serverName} Details
                    </h1>
                </div>
            </PageHeading>

            <Stack gap={3}>
                <Container>
                    <ApplicationServerManagementToast popupProps={popupProps} onClose={() => setPopupProps(undefined)} />
                    <AccordionWrapper
                        fitToSize={true}
                        isDefaultOpen={false}
                        headerIcon="fa-info-circle"
                        headerText="Server Details"
                        headerExtras={
                            <ActionButton
                                disabled={!userPrincipal.hasPermission(Permission.Server_RequestInfoUpdate)}
                                spinning={updateSpin}
                                icon="fa-refresh"
                                variant="btn-dark-blue"
                                title="Refresh"
                                onClick={(e: React.MouseEvent) => {
                                    AppManagerClient.refreshServerInfo(server.name);
                                    e.stopPropagation();
                                }}
                            />
                        }
                    >
                        <ServerInfoView userPrincipal={userPrincipal} server={server} isLoading={serverIsLoading} errorMessage={serverErrorMessage} isDataNotFound={!server} />
                    </AccordionWrapper>
                </Container>

                <Container>
                    <ServerPermissions serverName={serverName} userPrincipal={userPrincipal} />
                </Container>

                <Container>
                    <ServerAgents serverName={serverName} />
                </Container>

                <Container>
                    <AccordionWrapper
                        isDefaultOpen={false}
                        headerIcon="fa-scroll"
                        headerText="Certificates"
                        objectName={serverName}
                        isLoading={certificatesAreLoading}
                        isDataNotFound={certificates?.length === 0}
                        errorMessage={serverErrorMessage}
                        fitToSize={true}
                    >
                        <CertificatesEnhancedTable
                            isLoading={certificatesAreLoading}
                            data={certificates}
                            showServers={false}
                            useAutoHeight={true}
                            areCertsReceived={!certificatesAreLoading}
                        />
                    </AccordionWrapper>
                </Container>

                <Container>
                    <ServerApplicationListing serverName={serverName} />
                </Container>

                <Container>
                    <ServerEventLog serverName={serverName} />
                </Container>
            </Stack>
        </ScrollPageContent>
    );
}, Permission.Root_Servers);
